import { useEffect, useState } from 'react'
import { Building2, Plus, Pencil, Trash2, CheckCircle, AlertCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  listSponsors,
  createSponsor,
  updateSponsor,
  deleteSponsor,
  type AdvertiserSponsorItem,
} from '@/lib/api/advertiser'

const TIERS = ['gold', 'diamond', 'platinum'] as const

export function AdvertiserSponsors() {
  const [sponsors, setSponsors] = useState<AdvertiserSponsorItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<AdvertiserSponsorItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<{
    name: string
    website: string
    description: string
    tier: string
    logo: string
    isVerified: boolean
  }>({ name: '', website: '', description: '', tier: 'gold', logo: '', isVerified: false })

  async function reload() {
    try {
      setLoading(true)
      const res = await listSponsors({ limit: 200 })
      setSponsors(res.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sponsors')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  function openCreate() {
    setEditing(null)
    setForm({ name: '', website: '', description: '', tier: 'gold', logo: '', isVerified: false })
    setModalOpen(true)
  }

  function openEdit(s: AdvertiserSponsorItem) {
    setEditing(s)
    setForm({
      name: s.name,
      website: s.website ?? '',
      description: s.description ?? '',
      tier: s.tier ?? 'gold',
      logo: s.logo ?? '',
      isVerified: s.isVerified ?? false,
    })
    setModalOpen(true)
  }

  async function handleSave() {
    if (!form.name.trim()) return
    setSaving(true)
    try {
      if (editing) {
        await updateSponsor(editing.sponsorId, {
          name: form.name,
          website: form.website || undefined,
          description: form.description || undefined,
          tier: form.tier,
          logo: form.logo || undefined,
          isVerified: form.isVerified,
        })
        setNotice(`Sponsor "${form.name}" updated.`)
      } else {
        await createSponsor({
          name: form.name,
          website: form.website || undefined,
          description: form.description || undefined,
          tier: form.tier,
          logo: form.logo || undefined,
          isVerified: form.isVerified,
        })
        setNotice(`Sponsor "${form.name}" created.`)
      }
      setModalOpen(false)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(s: AdvertiserSponsorItem) {
    if (!window.confirm(`Delete sponsor "${s.name}"? This cannot be undone.`)) return
    try {
      await deleteSponsor(s.sponsorId)
      setNotice(`Sponsor "${s.name}" deleted.`)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sponsors</h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage brand partners and their tier status.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#D72638] hover:bg-[#D72638]/90 text-white w-fit"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add sponsor
        </Button>
      </div>

      {/* Notices */}
      {notice && (
        <div className="rounded-lg border border-green-700 bg-green-900/20 px-4 py-2 flex items-center gap-2 text-sm text-green-300">
          <CheckCircle className="w-4 h-4 shrink-0" />
          {notice}
          <button onClick={() => setNotice(null)} className="ml-auto text-green-400 hover:text-green-200">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/20 px-4 py-2 flex items-center gap-2 text-sm text-red-300">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-200">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p className="text-sm text-gray-500 animate-pulse">Loading sponsors…</p>
      ) : sponsors.length === 0 ? (
        <EmptyState onAdd={openCreate} />
      ) : (
        <div className="rounded-xl border border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/50">
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Sponsor
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium hidden md:table-cell">
                  Tier
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium hidden lg:table-cell">
                  Website
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium hidden md:table-cell">
                  Verified
                </th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sponsors.map((s) => (
                <tr
                  key={s.sponsorId}
                  className="border-b border-gray-900 hover:bg-white/[0.02] transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {s.logo ? (
                        <img
                          src={s.logo}
                          alt={s.name}
                          className="w-8 h-8 rounded object-contain bg-gray-800"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded bg-gray-800 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-gray-500" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-white">{s.name}</p>
                        {s.description && (
                          <p className="text-xs text-gray-500 truncate max-w-[200px]">
                            {s.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <TierBadge tier={s.tier} />
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {s.website ? (
                      <a
                        href={s.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#D72638] hover:underline text-xs"
                      >
                        {s.website.replace(/^https?:\/\//, '')}
                      </a>
                    ) : (
                      <span className="text-gray-600 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {s.isVerified ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-400">
                        <CheckCircle className="w-3 h-3" /> Verified
                      </span>
                    ) : (
                      <span className="text-xs text-gray-600">Unverified</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(s)}
                        className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(s)}
                        className="p-1.5 rounded hover:bg-red-900/30 text-gray-400 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-gray-800 bg-[#0a0a0a] p-6 space-y-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editing ? 'Edit sponsor' : 'Add sponsor'}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="text-gray-500 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Name *</span>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Safaricom"
                  className="bg-gray-900 border-gray-700"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Website</span>
                <Input
                  value={form.website}
                  onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                  placeholder="https://example.com"
                  className="bg-gray-900 border-gray-700"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Logo URL</span>
                <Input
                  value={form.logo}
                  onChange={(e) => setForm((f) => ({ ...f, logo: e.target.value }))}
                  placeholder="https://cdn.example.com/logo.png"
                  className="bg-gray-900 border-gray-700"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Description</span>
                <Input
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Short brand description"
                  className="bg-gray-900 border-gray-700"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-400 mb-1 block">Tier</span>
                <select
                  value={form.tier}
                  onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value }))}
                  className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D72638]"
                >
                  {TIERS.map((t) => (
                    <option key={t} value={t}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isVerified}
                  onChange={(e) => setForm((f) => ({ ...f, isVerified: e.target.checked }))}
                  className="rounded border-gray-600 bg-gray-800 text-[#D72638]"
                />
                <span className="text-sm text-gray-300">Mark as verified partner</span>
              </label>
            </div>

            <div className="flex gap-3 pt-1">
              <Button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="flex-1 bg-[#D72638] hover:bg-[#D72638]/90 text-white"
              >
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create sponsor'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setModalOpen(false)}
                className="border-gray-700 text-gray-300 hover:bg-white/5"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function TierBadge({ tier }: { tier?: string }) {
  const map: Record<string, string> = {
    gold: 'bg-yellow-500/15 text-yellow-400',
    diamond: 'bg-cyan-500/15 text-cyan-400',
    platinum: 'bg-purple-500/15 text-purple-400',
  }
  const cls = map[tier ?? ''] ?? 'bg-gray-700/30 text-gray-400'
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${cls}`}>
      {tier ?? 'none'}
    </span>
  )
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-700 p-10 text-center space-y-3">
      <Building2 className="w-8 h-8 text-gray-600 mx-auto" />
      <p className="text-sm text-gray-400">No sponsors yet.</p>
      <Button
        onClick={onAdd}
        variant="secondary"
        className="border-gray-700 text-gray-300 hover:bg-white/5"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add your first sponsor
      </Button>
    </div>
  )
}
